import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  AnalyticsNumericDataPointType,
  AnalyticsScalarMetric,
} from '../../../../models/api/analyticsMetricsModel';
import {getMetricRenderer} from '../../../helpers/analyticsFormatHelpers';
import {isZeroValidForScalarMetric} from '../../../helpers/isZeroValidForScalarMetric';
import {renderPercentageValue} from '../../../helpers/metricRenderers/percentMetricRenderer';

/*
 * Props.
 */

export interface MetricVariationProps {
  metricData: AnalyticsScalarMetric;
  className?: string;
  displayRawVariation?: boolean;
  centerRawVariation?: boolean;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div<{$color: string; $centerRawVariation: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$centerRawVariation ? 'center' : 'flex-end')};
  gap: 6px;
  color: ${(p) => p.$color};
`;

const StyledVariationDiv = styled.div`
  ${flexCenter}
  gap: 4px;
`;

const StyledIcon = styled(Icon)<{$backgroundColor?: string}>`
  background-color: ${(p) => p.$backgroundColor ?? 'transparent'};
  border-radius: 50%;
`;

/*
 * Component.
 */

export const MetricVariation: FC<MetricVariationProps> = ({
  metricData,
  className,
  displayRawVariation,
  centerRawVariation = false,
}) => {
  const theme = useUserTheme();
  const {value = 0, previousValue = 0} = metricData;

  const pctVariation = (value - previousValue) / previousValue;
  const rawVariation = value - previousValue;

  const isNumber = !isNaN(pctVariation);
  const isFiniteVariation = isFinite(pctVariation);

  const isZeroValid = isZeroValidForScalarMetric(metricData);

  const shouldDisplayVariation = isNumber && (isZeroValid || value !== 0);

  const {icon, color, backgroundColor} = getVariationConfig(shouldDisplayVariation, pctVariation, theme);

  return (
    <StyledWrapperDiv $color={color} className={className} $centerRawVariation={centerRawVariation}>
      <StyledVariationDiv>
        {shouldDisplayVariation && (
          <>
            {icon && <StyledIcon name={icon} color={color} $backgroundColor={backgroundColor} />}
            {isFiniteVariation && renderPercentageValue({value: Math.abs(pctVariation), isCompact: true})}
          </>
        )}
        {!shouldDisplayVariation && '- %'}
      </StyledVariationDiv>
      {shouldDisplayVariation && maybeDisplayRawVariation(displayRawVariation, rawVariation, metricData.type)}
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function getVariationConfig(
  shouldDisplayVariation: boolean,
  variation: number,
  theme: UserTheme,
): {color: string; backgroundColor?: string; icon?: IconName} {
  if (!shouldDisplayVariation || isNaN(variation) || variation === 0) {
    return {
      color: theme.greys.shade80,
    };
  }

  if (variation > 0) {
    return {
      backgroundColor: theme.palette[PaletteColorsEnum.TEAL].shade20,
      color: theme.palette[PaletteColorsEnum.TEAL].shade50,
      icon: 'arrowUpThickSmall',
    };
  }

  return {
    backgroundColor: theme.palette[PaletteColorsEnum.PURPLE].shade20,
    color: theme.palette[PaletteColorsEnum.PURPLE].shade50,
    icon: 'arrowDownThickSmall',
  };
}

function maybeDisplayRawVariation(
  displayRawVariation: boolean | undefined,
  rawVariation: number,
  metricType: AnalyticsNumericDataPointType,
): ReactNode {
  if (!displayRawVariation) {
    return null;
  }

  if (rawVariation === 0) {
    return '+0';
  }

  const metricRenderer = getMetricRenderer(metricType);
  return (
    <>
      {rawVariation > 0 && '+'}
      {metricRenderer({
        value: rawVariation,
        isCompact: true,
        compactThreshold: 100_000,
        isRawVariation: true,
      })}
    </>
  );
}
